import { ProductDetailPageResult } from "../ProductDetailPageResult";

/**
 * Extracts and flattens specification types from product detail template - specification table,
 * returning a record where each specification name maps to its corresponding value(s).
 * If multiple values exist for the same name, they are concatenated as a comma-separated string.
 *
 * @param activeTemplate - The product detail page result containing the specification table.
 * @returns A record mapping specification names to their string values.
 *
 * @example
 * const template = {
 *   specificationTable: {
 *     specifications: [
 *       {
 *         types: [
 *           { name: "Color", value: "Red" },
 *           { name: "Material", value: ["Steel", "Plastic"] }
 *         ]
 *       },
 *       {
 *         types: [
 *           { name: "Color", value: "Blue" }
 *         ]
 *       }
 *     ]
 *   }
 * };
 * const specs = getSpecifications(template);
 * // specs = { Color: "Red, Blue", Material: "Steel, Plastic" }
 */
export function getSpecifications(activeTemplate: ProductDetailPageResult) {
  return activeTemplate.specificationTable?.specifications
    ? activeTemplate.specificationTable.specifications
        .flatMap((specification) =>
          (specification.types || []).map((specificationType) => {
            const { name, value } = specificationType as {
              name: string;
              value: string | string[];
            };
            return name && value ? { name, value } : null;
          }),
        )
        .filter(Boolean)
        .reduce(
          (result, specificationType) => {
            if (specificationType?.name && specificationType?.value) {
              let val = specificationType.value;
              if (Array.isArray(val)) {
                val = val.join(", ");
              }
              if (result[specificationType.name]) {
                result[specificationType.name] =
                  `${result[specificationType.name]}, ${val}`;
              } else {
                result[specificationType.name] = val;
              }
            }
            return result;
          },
          {} as Record<string, string>,
        )
    : {};
}
